import _ from 'lodash';
import { parseAmount } from '../../../services';
import { Amount } from '../../../api';
import {
    getInstitutionalServiceLineKey,
    getPaymentItemKey,
    getProfessionalServiceLineKey,
} from '../matching-keys';
import { createProfessionalPaymentItemMatcher } from '../professional-payment-matching';
import { getPatientResponsibilityAmount, getPaymentsWithoutReversals } from '../payment-summary';

/**
 * Claim-level financial totals.
 *
 * These MUST equal the summary row of the Record of Services table: same payments (reversals removed first,
 * as `RecordOfServices.payments` does), same payment-item matching, same per-line rules, same totals
 * (matched service lines only). If the table's rules change, change them here too.
 */

export interface ClaimFinancialsPaymentItem {
    controlNumber?: string;
    procedureCode?: string;
    modifierCodes?: string[];
    fromDos?: string;
    paidAmount?: Amount;
    allowedAmount?: Amount;
}

export interface ClaimFinancialsPayment {
    id?: string;
    status?: string;
    created?: string;
    payerControlNumber?: string;
    allowedAmount?: Amount;
    payerAmount?: Amount;
    chargeAmount?: Amount;
    patientAmount?: Amount;
    items?: ClaimFinancialsPaymentItem[];
}

export interface ClaimFinancialsChargemasterItem {
    id?: string;
    accepted?: Amount;
}

export interface ProfessionalServiceLine {
    x12ControlNumber?: string;
    f24dProcedureCode?: string;
    f24dProcedureModifiers?: string[];
    f24aFromDateOfService?: string;
    f24fChargeAmount?: Amount;
    f24gUnits?: number;
    chargemasterItemId?: string;
}

export interface InstitutionalServiceLine {
    x12ControlNumber?: string;
    f44ProcedureCode?: string;
    f44ModifierCodes?: string[];
    f45FromServiceDate?: string;
    f47TotalChargeAmount?: Amount;
    f46ServiceUnits?: number;
    chargemasterItemId?: string;
}

export interface ClaimFinancials {
    charge: number;
    contracted: number;
    payerAllowed: number;
    payerPaid: number;
    patientResponsibility: number;
    patientPaid: number;
}

export { getPaymentItemKey, getProfessionalServiceLineKey, getInstitutionalServiceLineKey };

interface ServiceData {
    charge: number;
    accepted: number;
    allowed: number;
    payerPaid: number;
}

type PaymentItemWithPaymentId = ClaimFinancialsPaymentItem & { paymentId?: string };

const getPaymentItems = (payments: ClaimFinancialsPayment[]): PaymentItemWithPaymentId[] =>
    _.flatMap(payments, p => (p.items || []).map(item => ({ ...item, paymentId: p.id })));

const calculateServiceData = (
    foundPaymentItems: ClaimFinancialsPaymentItem[],
    charge: number,
    accepted: number,
): ServiceData => {
    const payerPaid = foundPaymentItems.reduce((sum, item) => sum + parseAmount(item.paidAmount), 0);
    // The largest allowed amount among the line's unique payment items (an empty list gives -Infinity -> 0).
    const allowed = Math.max(...foundPaymentItems.map(item => parseAmount(item.allowedAmount)));

    return { charge, accepted, allowed: isFinite(allowed) ? allowed : 0, payerPaid };
};

const calculateTotals = (
    services: ServiceData[],
    payments: ClaimFinancialsPayment[],
    patientPaid: Amount | undefined,
): ClaimFinancials => ({
    charge: _.sumBy(services, service => service.charge || 0),
    contracted: _.sumBy(services, service => service.accepted || 0),
    payerAllowed: _.sumBy(services, service => service.allowed || 0),
    payerPaid: _.sumBy(services, service => service.payerPaid || 0),
    patientResponsibility: getPatientResponsibilityAmount(payments),
    patientPaid: parseAmount(patientPaid),
});

/**
 * Totals from professional service lines.
 * `patientPaid` is the claim's patient-paid amount (`claim.patientInvoicePaidAmount`), as in Record of Services.
 */
export function calculateClaimFinancialsFromProfessionalServices(
    serviceLines: ProfessionalServiceLine[],
    rawPayments: ClaimFinancialsPayment[],
    patientPaid: Amount | undefined,
    chargemasterItems: ClaimFinancialsChargemasterItem[],
): ClaimFinancials {
    // Filter once, before matching AND totals: a reversal and the payment it voids must contribute to neither.
    const payments = getPaymentsWithoutReversals(rawPayments);
    const professionalMatcher = createProfessionalPaymentItemMatcher(getPaymentItems(payments));
    const paymentItemsByLine = professionalMatcher.matchLines(serviceLines);

    const services = serviceLines.map((line, lineIndex) => {
        const chargemasterItem = _.find(chargemasterItems, { id: line.chargemasterItemId });
        const charge = parseAmount(line.f24fChargeAmount);
        const accepted = parseAmount(chargemasterItem?.accepted) * (line.f24gUnits || 1);

        return calculateServiceData(paymentItemsByLine[lineIndex] || [], charge, accepted);
    });

    return calculateTotals(services, payments, patientPaid);
}

/**
 * Totals from institutional service lines.
 * `patientPaid` is the claim's patient-paid amount (`claim.patientInvoicePaidAmount`), as in Record of Services.
 */
export function calculateClaimFinancialsFromInstitutionalServices(
    serviceLines: InstitutionalServiceLine[],
    rawPayments: ClaimFinancialsPayment[],
    patientPaid: Amount | undefined,
    chargemasterItems: ClaimFinancialsChargemasterItem[],
): ClaimFinancials {
    // Filter once, before matching AND totals: a reversal and the payment it voids must contribute to neither.
    const payments = getPaymentsWithoutReversals(rawPayments);
    const existingControlNumbers = serviceLines.map(line => line.x12ControlNumber);

    const itemsMap = new Map<string | undefined, PaymentItemWithPaymentId[]>();
    getPaymentItems(payments).forEach(item => {
        const key = getPaymentItemKey(item, existingControlNumbers) || 'NOT_FOUND';
        itemsMap.set(key, [...(itemsMap.get(key) || []), item]);
    });

    const services = serviceLines.map(line => {
        // Same lookup as Record of Services: by control number, then without it. Each key is consumed once.
        let lineKey = getInstitutionalServiceLineKey(line);
        let items = itemsMap.get(lineKey);
        if (!items) {
            lineKey = getInstitutionalServiceLineKey({ ...line, x12ControlNumber: undefined });
            items = itemsMap.get(lineKey);
        }
        if (items) {
            itemsMap.delete(lineKey);
        }

        const chargemasterItem = _.find(chargemasterItems, { id: line.chargemasterItemId });
        const charge = parseAmount(line.f47TotalChargeAmount);
        const accepted = parseAmount(chargemasterItem?.accepted) * (line.f46ServiceUnits || 1);

        return calculateServiceData(items || [], charge, accepted);
    });

    return calculateTotals(services, payments, patientPaid);
}

/** Zero totals for when no template is available. */
export function getEmptyClaimFinancials(): ClaimFinancials {
    return {
        charge: 0,
        contracted: 0,
        payerAllowed: 0,
        payerPaid: 0,
        patientResponsibility: 0,
        patientPaid: 0,
    };
}
